/**
 * Configuration for the 10 P0 SEO landing pages — keyword + intent driven.
 * Each entry produces a fully indexable, conversion-ready page via SeoLandingTemplate.
 *
 * Schema:
 *   slug, h1, subhead, keywords, description, primaryCta, features[], steps[], faqs[]
 */

const cta = {
  signup: { label: "Start Free — No Credit Card", href: "/auth" },
  freeBill: { label: "Create Free Invoice", href: "/auth" },
  watchDemo: { label: "Watch 2-min Demo", href: "/sample/invoice" },
};

export const SEO_LANDING_PAGES = [
  // -------------------- 1 --------------------
  {
    slug: "gst-invoice-generator",
    h1: "GST Invoice Generator India — Free, GSTR-Ready",
    subhead:
      "Create GST-compliant tax invoices in minutes with auto CGST/SGST/IGST split, HSN/SAC summary, e-sign-ready PDFs and instant payment links. Made for Indian businesses.",
    keywords:
      "gst invoice generator india, free gst invoice, gst invoice software, gst billing india, gstr-1 ready invoice",
    description:
      "Free GST Invoice Generator for Indian businesses. Auto CGST/SGST/IGST split, HSN summary, GSTR-1 ready exports, professional PDF in 30 seconds.",
    badge: "Most Popular",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Shield", title: "Auto CGST + SGST / IGST split", text: "Place of supply auto-detected from GSTINs — never wrong, never mixed." },
      { icon: "FileText", title: "HSN / SAC code summary", text: "Required if turnover > ₹5 Cr — auto-generated on every invoice." },
      { icon: "Layers", title: "GSTR-1 export", text: "One-click JSON / Excel ready for your CA or the GST portal." },
      { icon: "Send", title: "Share by Email / WhatsApp", text: "Public payment link embedded — clients pay before you call them." },
      { icon: "Eye", title: "Audit-safe edit trail", text: "Every revision logged with hash + timestamp. CA-grade compliance." },
      { icon: "Users", title: "Customer master sync", text: "Repeat clients auto-fill GSTIN, address, billing terms." },
    ],
    steps: [
      "Sign up (no credit card) and add your company GSTIN.",
      "Pick a customer or type new — GSTIN auto-validates state.",
      "Add line items, GST %, optional discount.",
      "Click ‘Save & Send’ — invoice + payment link goes out.",
    ],
    faqs: [
      { q: "Is this GST invoice generator really free?", a: "Yes. Our free plan lets you generate unlimited GST invoices forever. Upgrade only when you need branding removal, e-sign, or multi-user access." },
      { q: "Does it handle CGST + SGST and IGST automatically?", a: "Yes. We compare your GSTIN’s state code with your customer’s GSTIN — same state shows CGST + SGST, different state shows IGST. Never mixed." },
      { q: "Can my CA download the GSTR-1 directly?", a: "Yes. The CA dashboard exports GSTR-1 in JSON or Excel for any month with one click." },
      { q: "Do you support HSN/SAC summary?", a: "Yes — every invoice with turnover above the GST threshold auto-generates a compliant HSN summary table." },
      { q: "Is the invoice PDF ready for the GST portal?", a: "Yes. Mandatory fields, IRN-ready format, and digital signature support are all built-in." },
    ],
  },
  // -------------------- 2 --------------------
  {
    slug: "free-invoice-generator",
    h1: "Free Invoice Generator — No Sign-Up to Start",
    subhead:
      "Create unlimited professional invoices online for free. Send by Email, WhatsApp or share a payment link. Trusted by Indian SMBs, freelancers and agencies.",
    keywords: "free invoice generator india, free invoice maker, free billing software, online invoice tool",
    description:
      "Free Invoice Generator for Indian businesses. Unlimited invoices, professional PDF, email + WhatsApp sharing, payment links. No credit card needed.",
    badge: "Free Forever",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Zap", title: "30-second invoices", text: "Pick a template, add items, hit save — done." },
      { icon: "Send", title: "Email + WhatsApp share", text: "Send invoices and payment links from one click." },
      { icon: "FileText", title: "Professional PDFs", text: "Logo, signature, branded colour — looks like ₹50,000 software." },
      { icon: "Lock", title: "Bank-grade security", text: "Your data is encrypted, backed up, and never sold." },
      { icon: "Users", title: "Unlimited customers", text: "No cap. Build your client master and re-use forever." },
      { icon: "BarChart3", title: "Track who paid", text: "Auto status: Paid / Partial / Overdue — and gentle reminders." },
    ],
    steps: [
      "Sign up free — no credit card.",
      "Add your business name (no GSTIN needed for non-GST users).",
      "Create your first invoice in 30 seconds.",
      "Share by Email or WhatsApp — get paid faster.",
    ],
    faqs: [
      { q: "Is it really free with no credit card?", a: "Yes — no card, no trial expiry. The free plan is permanent for individual sellers and small shops." },
      { q: "Can I add my own logo and branding?", a: "Logo, signature, and brand colour are all on the free plan. Removing the small ‘Powered by InvoiceSetu’ footer is part of paid plans." },
      { q: "Will my invoices look professional?", a: "Yes. Our default template is identical to what enterprise software produces — clean, GST-compliant and branded." },
      { q: "Can I move to GST invoicing later?", a: "Yes — flip the toggle when your business gets a GSTIN; existing clients & invoices migrate automatically." },
    ],
  },
  // -------------------- 3 --------------------
  {
    slug: "quotation-maker",
    h1: "Quotation Maker Online — Win More Clients",
    subhead:
      "Create professional quotations, send them by share link, and let clients approve online. Auto-converts to GST invoice with milestone billing built-in.",
    keywords: "quotation maker, quotation generator india, online quotation software, proposal tool india",
    description:
      "Quotation Maker for Indian businesses. Send quotes via share link, accept online approval, auto-convert to invoice (full or milestone), track activity.",
    badge: "Sales Pipeline",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Send", title: "Public share link", text: "No login needed for your client. They click → review → approve." },
      { icon: "RefreshCw", title: "One-click invoice conversion", text: "Approved? Convert to invoice — full amount or milestone slice." },
      { icon: "Layers", title: "Milestone / phase billing", text: "₹10L deal? Bill 30/40/30 — quote stays open until fully invoiced." },
      { icon: "Eye", title: "Real-time activity trail", text: "See when clients view, approve, request changes." },
      { icon: "FileText", title: "Auto-numbered + audit-trail", text: "Q-2025-26/0042 format, full edit history per quote." },
      { icon: "Users", title: "Client master integration", text: "Repeat clients auto-fill — quote in 60 seconds." },
    ],
    steps: [
      "Add line items, GST, valid-until date.",
      "Click ‘Copy share link’ — paste in WhatsApp / Email.",
      "Client reviews in browser, clicks Approve / Reject / Request Changes.",
      "On approval → click Convert to Invoice → full or milestone.",
    ],
    faqs: [
      { q: "How is this different from sending a PDF quote?", a: "Clients can approve directly in the browser — no signing, no scanning. You see the moment they open and decide. Your sales cycle shrinks by days." },
      { q: "What is milestone billing?", a: "When a project has multiple payment phases (e.g. 30% advance, 40% delivery, 30% completion), you can bill each milestone separately while keeping one quotation open." },
      { q: "Can I send the same quote to multiple clients?", a: "Yes — clone-and-edit any quotation for new prospects. The share link is unique per quote." },
      { q: "Will my client need to sign up?", a: "No. The public quote link works without login or signup — friction-free approval." },
    ],
  },
  // -------------------- 4 --------------------
  {
    slug: "tax-invoice-format",
    h1: "Tax Invoice Format India (GST 2025) — Free Template",
    subhead:
      "Download or generate the official Indian Tax Invoice format with all 16 mandatory GST fields. Auto-numbered, HSN-compliant, signature-ready.",
    keywords: "tax invoice format india, gst tax invoice format, indian invoice template, b2b invoice format",
    description:
      "Free Tax Invoice format India compliant with GST 2025 rules. All 16 mandatory fields, HSN summary, CGST/SGST/IGST split, professional PDF.",
    badge: "GST 2025 Format",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Shield", title: "All 16 mandatory GST fields", text: "Supplier GSTIN, invoice no., date, recipient details, HSN, place of supply — never missing." },
      { icon: "FileText", title: "Sequential numbering", text: "Auto increment per financial year — auditor-approved." },
      { icon: "Layers", title: "HSN / SAC summary", text: "Mandatory above ₹5 Cr turnover; auto-included." },
      { icon: "Eye", title: "Digital signature support", text: "DSC + e-sign with hash for tamper-proof PDFs." },
      { icon: "Send", title: "Email / share link", text: "No more lost PDFs in WhatsApp." },
      { icon: "RefreshCw", title: "Edit-history trail", text: "Every change logged for GSTR-1 audit safety." },
    ],
    steps: [
      "Pick the Indian tax invoice template.",
      "Auto-fill from your saved company + customer master.",
      "Add line items — HSN, GST%, qty, rate.",
      "PDF generates with all mandatory fields highlighted.",
    ],
    faqs: [
      { q: "What are the 16 mandatory fields in a GST tax invoice?", a: "Name, address & GSTIN of supplier; consecutive serial number; date; recipient name, address & GSTIN; HSN/SAC; description, qty, total value, taxable value, rate of tax, amount of tax, place of supply, signature. We include all of these by default." },
      { q: "When is HSN summary mandatory?", a: "Turnover > ₹5 Cr — 6-digit HSN. Below — 4-digit HSN. We adapt automatically based on your settings." },
      { q: "Is the format valid for B2B and B2C both?", a: "Yes. The template adjusts: B2C invoices over ₹50,000 still need recipient name & address — we prompt you when needed." },
    ],
  },
  // -------------------- 5 --------------------
  {
    slug: "bill-format-small-business",
    h1: "Bill Format for Small Business — Free Template",
    subhead:
      "Simple, professional bill format for small businesses. Works for shops, traders, freelancers — with or without GST. Free forever.",
    keywords: "bill format for small business, simple bill format india, shop bill format, retail bill template",
    description:
      "Free Bill Format for Small Business in India. Simple, professional bills for shops, traders, freelancers. With or without GST. Generate in 30 seconds.",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Zap", title: "Bill in 30 seconds", text: "No accounting jargon. Just type and print." },
      { icon: "FileText", title: "GST optional", text: "Don’t have a GSTIN? Use ‘Bill of Supply’ format — fully legal." },
      { icon: "IndianRupee", title: "₹ amount in words", text: "Auto-converts ₹12,450 → ‘Twelve Thousand Four Hundred Fifty Rupees Only’." },
      { icon: "Send", title: "Print or share", text: "Print on A4/A5, send by WhatsApp, or generate a payment link." },
      { icon: "Users", title: "Repeat customer fill", text: "Frequent buyer? Bill in one click." },
      { icon: "BarChart3", title: "Daily sales summary", text: "End of day, see total sales, GST collected, pending payments." },
    ],
    steps: [
      "Add your shop / business name.",
      "Pick GST or Non-GST mode (toggle anytime).",
      "Type item, qty, rate — bill is ready.",
      "Print or send by WhatsApp.",
    ],
    faqs: [
      { q: "Do I need a GSTIN to use this?", a: "No. If you don’t have GST, the format auto-switches to ‘Bill of Supply’ which is the right format for non-GST sellers." },
      { q: "Can I print on A5 or thermal printer?", a: "A4 and A5 both supported. Thermal (58mm/80mm) printer template is on the paid plan." },
      { q: "Can I add my logo?", a: "Yes — logo and signature on every bill, even on the free plan." },
    ],
  },
  // -------------------- 6 --------------------
  {
    slug: "proforma-invoice-generator",
    h1: "Proforma Invoice Generator — Free, Online",
    subhead:
      "Issue proforma invoices to confirm orders before delivery. Convert to tax invoice in one click when goods are dispatched.",
    keywords: "proforma invoice generator, proforma invoice format india, pi format, proforma vs tax invoice",
    description:
      "Free Proforma Invoice Generator. Issue PI before delivery, convert to GST tax invoice on dispatch. Indian-format compliant.",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "FileText", title: "Marked PROFORMA clearly", text: "No tax credit confusion — clearly labelled to keep auditors happy." },
      { icon: "RefreshCw", title: "Convert to tax invoice", text: "Order shipped? Click convert — keeps a link for traceability." },
      { icon: "Send", title: "Share for approval", text: "Email / WhatsApp / public link — accept advance before producing." },
      { icon: "Eye", title: "Validity period", text: "Auto-expire when terms aren’t accepted in time." },
      { icon: "Users", title: "Customer master sync", text: "Repeat-buyer details fill instantly." },
      { icon: "Lock", title: "Audit trail", text: "Every revision tracked end-to-end." },
    ],
    steps: [
      "Create a proforma invoice with terms + validity.",
      "Send share link — buyer reviews + approves.",
      "Receive advance.",
      "Dispatch → click ‘Convert to Tax Invoice’ → done.",
    ],
    faqs: [
      { q: "Can a proforma invoice claim GST input?", a: "No. A proforma invoice is not a tax invoice — buyers cannot claim ITC against it. We label it clearly to avoid confusion." },
      { q: "When should I use proforma vs quotation?", a: "Quotation = price offer. Proforma = confirmed order awaiting dispatch. Many businesses use both: quote first, proforma after acceptance." },
      { q: "Does the proforma auto-numbered separately?", a: "Yes — separate sequence (e.g. PI/2026-27/001) so it never clashes with your tax invoice numbers." },
    ],
  },
  // -------------------- 7 --------------------
  {
    slug: "freelancer-invoice-tool",
    h1: "Freelancer Invoice Tool India — Get Paid Faster",
    subhead:
      "Send beautiful invoices to clients, accept payments online, track follow-ups, and never chase overdue payments again.",
    keywords: "freelancer invoice india, invoice software for freelancers, freelance billing india, designer invoice tool",
    description:
      "Invoice tool built for Indian freelancers — designers, developers, writers, consultants. Online payments, auto-reminders, GST optional.",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Zap", title: "30-second invoice", text: "From draft to ‘sent’ in under a minute." },
      { icon: "IndianRupee", title: "Online payment links", text: "Razorpay UPI / card — clients pay before you call them." },
      { icon: "RefreshCw", title: "Auto reminders", text: "Polite WhatsApp / email reminders on day 3, 7, 14." },
      { icon: "BarChart3", title: "Recurring retainers", text: "Monthly retainer? Bill auto-runs every 1st." },
      { icon: "FileText", title: "GST optional", text: "Below ₹20L turnover? Skip GST entirely." },
      { icon: "Users", title: "International clients", text: "Multi-currency, USD/EUR/GBP — show INR equivalent." },
    ],
    steps: [
      "Sign up — no GSTIN needed under ₹20L turnover.",
      "Save 3-5 frequent clients in your master.",
      "Send your first invoice with payment link.",
      "Sleep peacefully — reminders go out automatically.",
    ],
    faqs: [
      { q: "I freelance under ₹20L — do I need GST?", a: "No. Below ₹20L turnover (₹10L for special states), GST registration is optional. Use the Bill of Supply mode." },
      { q: "Can I bill foreign clients?", a: "Yes — multi-currency invoices with INR conversion shown for FIRC/FEMA records." },
      { q: "Does it handle TDS deduction?", a: "Yes. Mark invoices subject to TDS — net payable auto-calculates after the deduction." },
    ],
  },
  // -------------------- 8 --------------------
  {
    slug: "invoice-software-traders",
    h1: "Invoice Software for Traders & Wholesalers",
    subhead:
      "Built for trading businesses with high invoice volume, multi-godown stock, party ledger, and credit-cycle tracking.",
    keywords: "invoice software for traders, wholesale billing software, trader gst software, b2b invoice india",
    description:
      "Invoice & billing software for Indian traders / wholesalers. Multi-party ledger, credit-cycle tracking, GST + e-invoicing, bulk import.",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Layers", title: "Bulk invoice import", text: "Excel upload — 200 invoices in 2 minutes." },
      { icon: "Users", title: "Party ledger", text: "Outstanding balance, ageing, reminders per buyer." },
      { icon: "TrendingUp", title: "Credit-cycle dashboard", text: "Who owes what, since when, days overdue." },
      { icon: "FileText", title: "E-invoicing ready", text: "IRN + QR code for ₹5 Cr+ turnover (coming soon)." },
      { icon: "Send", title: "WhatsApp dispatch", text: "Send LR/POD + invoice in one click to the buyer." },
      { icon: "Shield", title: "Bulk GSTR-1", text: "One-click monthly export for your CA." },
    ],
    steps: [
      "Import existing customers + product list from Excel.",
      "Set credit terms per party (Net 30 / 45 / 60).",
      "Generate invoice — system tracks ageing automatically.",
      "Get a daily ‘Who-owes-what’ summary in your inbox.",
    ],
    faqs: [
      { q: "Can I import 500+ products at once?", a: "Yes. Use the Excel import wizard — products, customers, opening balances all import together." },
      { q: "Do you support party-wise outstanding ageing?", a: "Yes. The Receivables dashboard breaks down each party’s overdue into 0–30, 31–60, 61–90, 90+ buckets." },
      { q: "Will it integrate with my Tally?", a: "Yes — Tally XML import/export is on the Pro plan. Talk to our team if you need a custom mapping." },
    ],
  },
  // -------------------- 9 --------------------
  {
    slug: "billing-app-shop-owners",
    h1: "Billing App for Shop Owners — Print + Mobile",
    subhead:
      "Lightweight billing app for shopkeepers. Bill from your desktop or phone, print on a thermal printer, accept UPI — all in one tap.",
    keywords: "billing app for shop owners, kirana billing app, retail pos india, mobile billing app",
    description:
      "Billing app for Indian shop owners — kirana, retail, restaurant. Bill on desktop or phone, thermal printing, UPI accept, daily sales report. Free to start.",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "Zap", title: "Bill from your phone", text: "Faster than punching into a billing machine." },
      { icon: "FileText", title: "Thermal printer support", text: "58mm / 80mm Bluetooth — no extra software." },
      { icon: "IndianRupee", title: "UPI on the bill", text: "Dynamic QR per bill — customer scans + pays." },
      { icon: "BarChart3", title: "Daily Z report", text: "End of day: total sales, GST, cash vs UPI vs card." },
      { icon: "Layers", title: "Stock + barcode", text: "Scan barcode → bill → stock auto-deducted." },
      { icon: "Users", title: "Customer credit book", text: "Replace the udhaar khata — track who owes what." },
    ],
    steps: [
      "Install on your phone (PWA — no Play Store needed).",
      "Add 50-100 fast-moving items.",
      "Scan barcode or tap → bill ready.",
      "Print or send WhatsApp bill — customer pays via QR.",
    ],
    faqs: [
      { q: "Does it work without internet?", a: "Yes — offline mode keeps billing running. Sales sync the moment internet is back." },
      { q: "Can I use it on my existing thermal printer?", a: "Yes — most Bluetooth thermal printers (58mm / 80mm) work out of the box." },
      { q: "Is the UPI QR mine or yours?", a: "Yours. Add your UPI ID once — every bill shows a dynamic QR routing to your account." },
    ],
  },
  // -------------------- 10 --------------------
  {
    slug: "non-gst-bill-generator",
    h1: "Non-GST Bill Generator — Bill of Supply, Free",
    subhead:
      "Don’t have a GSTIN? Use the right format. Free Bill of Supply generator for businesses with turnover under ₹20L (₹10L special states).",
    keywords: "non gst bill generator, bill of supply format, non gst invoice india, composition scheme bill",
    description:
      "Free Non-GST Bill / Bill of Supply generator for Indian small businesses. Below ₹20L turnover, freelancers, composition scheme dealers.",
    primaryCta: cta.signup,
    secondaryCta: cta.watchDemo,
    features: [
      { icon: "FileText", title: "Correct legal label", text: "Marked ‘Bill of Supply’ — not ‘Tax Invoice’ — to stay compliant." },
      { icon: "Shield", title: "Composition scheme ready", text: "Includes the mandatory ‘composition taxable person’ note." },
      { icon: "RefreshCw", title: "Switch to GST anytime", text: "Crossed ₹20L? One click migrates everything to GST mode." },
      { icon: "Zap", title: "Same speed as GST invoices", text: "Bill in 30 seconds — same UI." },
      { icon: "Users", title: "Track repeat customers", text: "Build your client list as you grow." },
      { icon: "Send", title: "Email / WhatsApp share", text: "Professional PDF, no GSTIN required." },
    ],
    steps: [
      "Sign up — leave GSTIN blank.",
      "App auto-switches to ‘Bill of Supply’ mode.",
      "Add items — no GST column shown.",
      "Send by WhatsApp / Email — clean professional PDF.",
    ],
    faqs: [
      { q: "When can I use a Bill of Supply instead of a Tax Invoice?", a: "When you’re not registered for GST (turnover below ₹20L / ₹10L special-category states), or you’re a composition scheme dealer, or selling exempt goods." },
      { q: "Is a Bill of Supply legally valid?", a: "Yes — it’s the prescribed format under the GST Act for non-GST and composition dealers. We add all mandatory fields by default." },
      { q: "What if I cross ₹20L and need to switch?", a: "Add your GSTIN in Settings — every future invoice flips to Tax Invoice format. Old Bills of Supply stay as historical records." },
    ],
  },
];
